import type {
  CustomerInvoiceListRow,
  CustomerPaymentReceiptListRow,
  SupplierBillListRow,
  SupplierBillPaymentReceiptListRow,
} from "@sdk/accounting/accounting-list-report.models";
import type { iBooking } from "@sdk/bookings/booking-model";
import type { iContact, iHolidayEnquiry } from "@sdk/crm/crm.models";
import type { iHolidayQuoteX } from "@sdk/holiday-quotes-x/holiday-quotes-x-model";

import { formatAccountingDate, formatAccountingMoney } from "@/components/accounting/accounting-format";
import {
  deriveInvoiceListStatus,
  deriveSupplierBillListStatus,
  formatInvoiceSummaryPrimary,
  formatPaymentPlanLabel,
  getInvoiceCurrency,
  getInvoiceTotalAmount,
  getSupplierBillHomeCurrency,
  getSupplierBillTotalHomeCurrency,
} from "@/components/accounting/accounting-entity-helpers";
import { formatProductContextLabel } from "@/components/accounting/accounting-product-helpers";
import { formatBookingReference } from "@/components/accounting/table-cells";
import { getContactName } from "@/components/customers/helpers/get-contact-name";
import { GetContactIdentifier } from "@/components/customers/helpers/get-contact-identifier";

import type { GlobalSearchResult } from "./global-search.types";

function joinDetails(parts: Array<string | undefined | null>): string[] {
  return parts.map((part) => part?.trim()).filter(Boolean) as string[];
}

function formatDate(epoch?: number | null): string | undefined {
  if (!epoch) return undefined;
  return formatAccountingDate(epoch);
}

function formatDepartureDate(value?: string | number | null): string | undefined {
  if (value == null || value === "") return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return formatAccountingDate(date.getTime());
}

function formatBookingStatus(booking: iBooking): string | undefined {
  const value = booking.statusSummary ?? booking.status;
  const labels: Record<string, string> = {
    DRAFT: "Draft",
    SENT: "Sent",
    EXPIRED: "Expired",
    CONFIRMED: "Confirmed",
    AMENDMENT_REQUESTED: "Amendment requested",
    AMENDMENT_DRAFTED: "Amendment drafted",
    AMENDMENT_SENT: "Amendment sent",
  };
  return labels[value] ?? value;
}

function formatQuoteStatus(status?: string): string | undefined {
  if (!status) return undefined;
  const labels: Record<string, string> = {
    DRAFT: "Draft",
    SENT: "Sent",
    FINALIZED: "Finalized",
    CONFIRMED: "Confirmed",
  };
  return labels[status] ?? status;
}

function formatInvoiceStatus(invoice: CustomerInvoiceListRow): string {
  const status = deriveInvoiceListStatus(invoice);
  if (status === "PAID") return "Paid";
  if (status === "OPEN") return "Open";
  return "Draft";
}

function formatSupplierBillStatus(bill: SupplierBillListRow): string {
  const status = deriveSupplierBillListStatus(bill);
  const labels: Record<string, string> = {
    NOT_PAID: "Not paid",
    PARTIALLY_PAID: "Partially paid",
    FULLY_PAID: "Paid",
  };
  return labels[status] ?? status;
}

function formatBookingPrice(booking: iBooking): string | undefined {
  const breakdown = booking.pricingAndPayments?.draft?.pricingBreakdown;
  const amount =
    breakdown?.totalCostInHomeCurrency ?? breakdown?.totalCost ?? undefined;
  if (amount == null) return undefined;
  const currency =
    booking.homeCurrency ??
    breakdown?.homeCurrency ??
    breakdown?.sellingCurrency ??
    "GBP";
  return formatAccountingMoney(amount, currency);
}

function formatQuoteValue(quote: iHolidayQuoteX): string | undefined {
  if (quote.quoteValue?.amount == null) return undefined;
  return formatAccountingMoney(
    quote.quoteValue.amount,
    quote.quoteValue.currency ?? "GBP"
  );
}

export function formatEnquiryRef(shortId?: number) {
  if (shortId == null) return "Enquiry";
  return `ENQ-${shortId.toString().padStart(4, "0")}`;
}

export function formatQuoteRef(shortId?: number) {
  if (shortId == null) return "Quote";
  return `QTE-${shortId.toString().padStart(4, "0")}`;
}

export function mapCustomerResult(contact: iContact): GlobalSearchResult {
  return {
    id: contact.id!,
    type: "customer",
    title: getContactName(contact) || "Unnamed customer",
    subtitle: GetContactIdentifier(contact),
    details: joinDetails([
      contact.metaData?.createdTime
        ? `Created ${formatDate(contact.metaData.createdTime)}`
        : undefined,
      contact.data.country ? contact.data.country : undefined,
      contact.data.organization ? contact.data.organization : undefined,
    ]),
    badge: "Customer",
  };
}

export function mapEnquiryResult(enquiry: iHolidayEnquiry): GlobalSearchResult {
  return {
    id: enquiry.id!,
    type: "enquiry",
    title: formatEnquiryRef(enquiry.shortId),
    subtitle:
      enquiry.title?.trim() ||
      enquiry.data?.notes?.trim() ||
      enquiry.data?.enquiryType ||
      undefined,
    details: joinDetails([
      enquiry.pipelineStage ? `Stage: ${enquiry.pipelineStage}` : undefined,
      enquiry.data?.departureDate
        ? `Departs ${formatDepartureDate(enquiry.data.departureDate)}`
        : undefined,
      enquiry.data?.requestType ? enquiry.data.requestType : undefined,
      enquiry.createdAt ? `Created ${formatDate(enquiry.createdAt)}` : undefined,
    ]),
    badge: "Enquiry",
  };
}

export function mapQuoteResult(quote: iHolidayQuoteX): GlobalSearchResult {
  return {
    id: quote.id!,
    type: "quote",
    title: formatQuoteRef(quote.shortId),
    subtitle: quote.data?.title?.trim() || undefined,
    details: joinDetails([
      quote.data?.departureDate
        ? `Departs ${formatDepartureDate(quote.data.departureDate)}`
        : undefined,
      quote.data?.nights != null ? `${quote.data.nights} nights` : undefined,
      quote.metadata?.status
        ? formatQuoteStatus(quote.metadata.status)
        : undefined,
      quote.metadata?.createdAt
        ? `Created ${formatDate(quote.metadata.createdAt)}`
        : undefined,
    ]),
    trailing: formatQuoteValue(quote),
    badge: "Quote",
  };
}

export function mapBookingResult(booking: iBooking): GlobalSearchResult {
  const customerContact = [
    booking.enquiryData?.email,
    booking.enquiryData?.mobileNumber,
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    id: booking.id!,
    type: "booking",
    title:
      booking.bookingRef?.trim() || formatBookingReference(booking.shortId),
    subtitle: booking.enquiryData?.customerName || undefined,
    details: joinDetails([
      customerContact || undefined,
      booking.bookingData?.departureDate
        ? `Departs ${formatDepartureDate(booking.bookingData.departureDate)}`
        : undefined,
      formatBookingStatus(booking),
      booking.createdAt ? `Created ${formatDate(booking.createdAt)}` : undefined,
    ]),
    trailing: formatBookingPrice(booking),
    badge: "Booking",
  };
}

export function mapCustomerInvoiceResult(
  invoice: CustomerInvoiceListRow
): GlobalSearchResult {
  return {
    id: invoice.id!,
    type: "customer-invoice",
    title:
      invoice.bookingRef?.trim() ||
      formatBookingReference(invoice.booking?.shortId),
    subtitle: formatInvoiceSummaryPrimary(invoice),
    details: joinDetails([
      invoice.enquiryData?.customerName
        ? `Customer: ${invoice.enquiryData.customerName}`
        : undefined,
      invoice.createdAt ? `Created ${formatDate(invoice.createdAt)}` : undefined,
      formatInvoiceStatus(invoice),
      invoice.paymentPlan?.type
        ? formatPaymentPlanLabel(invoice.paymentPlan.type)
        : undefined,
    ]),
    trailing: formatAccountingMoney(
      getInvoiceTotalAmount(invoice),
      getInvoiceCurrency(invoice)
    ),
    badge: "Invoice",
    bookingId: invoice.bookingId ?? invoice.booking?.id,
    raw: invoice,
  };
}

export function mapSupplierBillResult(
  bill: SupplierBillListRow,
  suppliersMap: Record<string, { name?: string }>
): GlobalSearchResult {
  const supplierName =
    suppliersMap[bill.supplierId]?.name ?? bill.supplierId ?? "Unknown supplier";

  return {
    id: bill.id!,
    type: "supplier-bill",
    title: supplierName,
    subtitle: [
      bill.bookingRef?.trim() ||
        formatBookingReference(bill.booking?.shortId),
      formatProductContextLabel(bill.product, bill.productId),
    ]
      .filter(Boolean)
      .join(" · "),
    details: joinDetails([
      bill.billedAt ? `Billed ${formatDate(bill.billedAt)}` : undefined,
      formatSupplierBillStatus(bill),
      bill.supplierBookingReference
        ? `Ref: ${bill.supplierBookingReference}`
        : undefined,
    ]),
    trailing: formatAccountingMoney(
      getSupplierBillTotalHomeCurrency(bill),
      getSupplierBillHomeCurrency(bill)
    ),
    badge: "Supplier bill",
    bookingId: bill.bookingId ?? bill.booking?.id,
    raw: bill,
  };
}

export function mapCustomerPaymentReceiptResult(
  receipt: CustomerPaymentReceiptListRow
): GlobalSearchResult {
  return {
    id: receipt.id!,
    type: "customer-payment-receipt",
    title:
      receipt.bookingRef?.trim() ||
      formatBookingReference(receipt.booking?.shortId),
    subtitle: receipt.enquiryData?.customerName || undefined,
    details: joinDetails([
      receipt.addedAt ? `Paid ${formatDate(receipt.addedAt)}` : undefined,
      receipt.reference ? `Ref: ${receipt.reference}` : undefined,
      receipt.customerInvoiceId ? `Invoice linked` : undefined,
      receipt.isRefund ? "Refund" : receipt.voided ? "Void" : "Payment",
    ]),
    trailing: formatAccountingMoney(
      receipt.amountInHomeCurrency ?? 0,
      receipt.homeCurrency ?? "GBP"
    ),
    badge: "Payment",
    bookingId: receipt.bookingId ?? receipt.booking?.id,
    raw: receipt,
  };
}

export function mapSupplierBillPaymentReceiptResult(
  receipt: SupplierBillPaymentReceiptListRow
): GlobalSearchResult {
  return {
    id: receipt.id!,
    type: "supplier-bill-payment-receipt",
    title:
      receipt.bookingRef?.trim() ||
      formatBookingReference(receipt.booking?.shortId),
    subtitle: formatProductContextLabel(receipt.product, receipt.productId),
    details: joinDetails([
      receipt.addedAt ? `Paid ${formatDate(receipt.addedAt)}` : undefined,
      receipt.reference ? `Ref: ${receipt.reference}` : undefined,
      receipt.supplierBillId ? "Bill linked" : undefined,
      receipt.isRefund ? "Refund" : receipt.voided ? "Void" : "Payment",
    ]),
    trailing: formatAccountingMoney(
      receipt.amountInHomeCurrency ?? 0,
      receipt.homeCurrency ?? "GBP"
    ),
    badge: "Supplier payment",
    bookingId: receipt.bookingId ?? receipt.booking?.id,
    productId: receipt.productId,
    raw: receipt,
  };
}

export async function loadSuppliersMap(): Promise<
  Record<string, { name?: string }>
> {
  const { SDK } = await import("@sdk/sdk");
  try {
    const res = await SDK.suppliers.query({
      query: {},
      options: { page: 1, limit: 1000, offset: 0 },
    });
    return Object.fromEntries(
      (res.docs ?? [])
        .filter((supplier) => supplier.id)
        .map((supplier) => [supplier.id!, { name: supplier.name }])
    );
  } catch {
    return {};
  }
}
